import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ChartModule } from 'primeng/chart';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';
import { DecimalPipe } from '@angular/common';
import { UtilsService } from '../../../../../core/services/utils.service';

@Component({
  selector: 'app-gas-cost-breakdown-chart',
  standalone: true,
  imports: [ChartModule],
  providers: [DecimalPipe],
  templateUrl: './gas-cost-breakdown-chart.component.html',
  styleUrl: './gas-cost-breakdown-chart.component.css'
})
export class GasCostBreakdownChartComponent implements OnInit, OnChanges {
  data: any;
  options: any;

  // Dummy data
  @Input() showDummyData: boolean = false;
  dummyshowData: number[] = [0.25, 0.12, 0.30, 0.04, 0.09];
  dummytotalPricePerUnit: number = 2.5;

  label: string[] = ['Material Cost', 'Fixed Cost', 'TMS', 'Additional Charges', 'Taxes'];
  @Input() showData: number[] = [0.25, 0.12, 0.30, 0.04, 0.09];
  @Input() totalPricePerUnit: number = 2.5;
  backgroundColors: string[] = ['#5E8983', '#7EA4A4', '#324A4B', '#4D7771', '#6B8F8C'];

  constructor(private translocoService: TranslocoService, private decimalPipe: DecimalPipe, private utilsService: UtilsService) {

  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  ngOnInit(): void {
    this.updateChart();

    const labelKeys = [
      'dashboard.costs_per_smc_labels.material_costs',
      'dashboard.costs_per_smc_labels.fixed_costs',
      'dashboard.costs_per_smc_labels.tms',
      'dashboard.costs_per_smc_labels.additional_charges',
      'dashboard.costs_per_smc_labels.taxes'
    ];

    combineLatest(labelKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.label = translations;
      this.updateChart();
    });
  }


  updateChart(): void {
    if (this.showDummyData) {
      this.showData = this.dummyshowData;
      this.totalPricePerUnit = this.dummytotalPricePerUnit;
    }

    Chart.register(ChartDataLabels);

    const centerTextPlugin = {
      id: 'centerTextCBGas',
      beforeDraw(chart: any) {
        const pluginOptions = chart.config.options.plugins?.centerTextCBGas;
        if (!pluginOptions || pluginOptions.enabled === false) {
          return;
        }

        const ctx = chart.ctx;

        const { totalPricePerUnit = 0 } = pluginOptions;
        const { top, bottom, left, right } = chart.chartArea;
        const centerX = (left + right) / 2;
        const centerY = (top + bottom) / 2;

        const text = (totalPricePerUnit == 0) ? '' : `${totalPricePerUnit}\n€/smc`;

        ctx.save();
        ctx.font = 'bold 20px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        text.split('\n').forEach((line, index) => {
          ctx.fillText(line, centerX, centerY + index * 20);
        });

        ctx.restore();
      },
    };
    Chart.register(centerTextPlugin);

    this.data = {
      labels: this.label,
      datasets: [
        {
          data: this.showData,
          backgroundColor: this.backgroundColors,
          hoverBackgroundColor: this.backgroundColors,
          borderWidth: 0,
          borderRadius: 6,
          spacing: 2,
        },
      ],
    };

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: {
        centerTextCBGas: {
          enabled: true,
          totalPricePerUnit: this.decimalPipe.transform(this.totalPricePerUnit, '1.2-2') || this.totalPricePerUnit
        },
        legend: {
          display: false,
        },
        datalabels: {
          color: '#000',
          font: {
            size: 12,
            weight: 'normal',
            family: 'Gilroy',
          },
          formatter: (value: number, context: any) => {
            if (value === 0) {
              return '';
            }

            const dataset = context.chart.data.datasets[context.datasetIndex].data;
            const total = dataset.reduce((sum: number, val: number) => sum + val, 0);
            const percentage = (value / total) * 100;

            const label = context.chart.data.labels[context.dataIndex] || "";

            const wrappedLines = this.utilsService.getMultiLineLabels(label, 10);
            const percentText = `${this.decimalPipe.transform(percentage, '1.1-1')}%`;

            return [...wrappedLines, percentText];
          },
          align: 'end',
          anchor: 'end',
          padding: 10,
          clamp: true,
          display: true
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (tooltipItem: any) => {
              const dataset = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex].data;
              const total = dataset.reduce((sum: number, val: number) => sum + val, 0);
              const value = tooltipItem.raw;
              const percentage = (value / total) * 100;

              return `${this.decimalPipe.transform(percentage, '1.1-1')}%`;
            }
          }
        },
      },
      layout: {
        padding: 80,
      },
    };
  }
}
